import { pillarHref, pillars } from '../data/pillars'

type PageMeta = { title: string; description: string }

const defaultMeta: PageMeta = {
  title: 'SyNeural — Building intelligence in the world',
  description:
    'SyNeural builds systems that help people develop sound judgment, researchers connect ideas, and organizations turn knowledge into action.',
}

const pillarMeta: Record<string, PageMeta> = Object.fromEntries(
  pillars.map((pillar) => [
    pillarHref(pillar.id),
    { title: `${pillar.name} — NCJI pillar ${pillar.order} | SyNeural`, description: pillar.description },
  ]),
)

const pageMeta: Record<string, PageMeta> = {
  ...pillarMeta,
  '/': {
    title: 'SyNeural — Connected knowledge. Stronger judgment. Greater human capability.',
    description:
      'SyNeural connects governed knowledge, structured capability development and practical action through the Semantic Knowledge Platform, Human Capability Framework and Noetivra workspace.',
  },
  '/human-capability': {
    title: 'Human Capability Framework (HCF) — SyNeural',
    description: 'Developing reasoning, judgment and readiness for responsibility through structured assessment, human review and development.',
  },
  '/ncji': {
    title: 'NCJI — Five pillars of sound judgment | SyNeural',
    description: 'The Neurocognitive Civic Judgment Index: the five-pillar judgment assessment and development model within the Human Capability Framework.',
  },
  '/noetivra': {
    title: 'Noetivra — One workspace for capability, support and institutional action | SyNeural',
    description: 'Noetivra brings SyNeural’s connected knowledge and human capability systems into domain-specific workspaces. Explore the Demo Center.',
  },
  '/noetivra/demo-center': {
    title: 'Noetivra Demo Center | SyNeural',
    description:
      'Explore Noetivra’s domain-specific workspaces for government, research, education, employers, communities, leadership development and clinical review.',
  },
  '/demos/government': {
    title: 'Government demo · Noetivra Demo Center | SyNeural',
    description: 'Open the Noetivra Government and National Capability and Development System demonstration.',
  },
  '/demos/research': {
    title: 'Noetivra Research demo | SyNeural',
    description: 'From sources to understanding: explore fictional studies, propose and review relationships, and inspect the evidence behind a knowledge map.',
  },
  '/demos/education': {
    title: 'Noetivra for Education demo | SyNeural',
    description: 'Build reasoning and self-direction through evidence, feedback and practice. An interactive prototype with fictional records.',
  },
  '/demos/talent': {
    title: 'Noetivra Talent demo | SyNeural',
    description: 'Connect role requirements to work samples, transparent review and development, with human decisions kept separate.',
  },
  '/demos/leadership': {
    title: 'Noetivra Leadership demo | SyNeural',
    description: 'Make consequential decisions inspectable, then connect feedback to coached practice.',
  },
  '/demos/community': {
    title: 'Noetivra for Communities demo | SyNeural',
    description: 'Connect personal goals, mentorship and practical support through respectful participation and purpose-limited referrals.',
  },
  '/demos/clinical': {
    title: 'Noetivra Clinical scoped demo | SyNeural',
    description: 'A clearer clinical picture through structured documentation and professional review. Documentation only — not diagnosis.',
  },
  '/government': {
    title: 'National Capability and Development System — SyNeural',
    description: 'SyNeural’s proposed National Capability and Development System connects knowledge, assessments, development plans, public services and program evaluation.',
  },
  '/research': {
    title: 'Research — SyNeural',
    description:
      'The research questions, papers, assessment methods, and evidence behind SyNeural’s systems and projects.',
  },
  '/projects': {
    title: 'Projects — SyNeural',
    description:
      'Named projects built using SyNeural’s connected knowledge and human capability systems, starting with Cognocracy.',
  },
  '/projects/cognocracy': {
    title: 'Cognocracy — A proposed civic framework | SyNeural',
    description:
      'Cognocracy is a concept proposal powered by SyNeural, exploring how structured assessment, capability development, and verified credentials could support institutional trust and readiness for public responsibility.',
  },
}

export function applyPageMeta(path: string) {
  const meta = pageMeta[path] ?? defaultMeta
  document.title = meta.title
  setMeta('name', 'description', meta.description)
  setMeta('property', 'og:title', meta.title)
  setMeta('property', 'og:description', meta.description)
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}
